import { useState } from 'react';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, MessageCircle, Linkedin, Youtube, Instagram } from 'lucide-react';
import { BRAND } from '@/data/mockData';
import { CONTACT } from '@/constants/testIds';
import ParticlesBg from '@/components/common/ParticlesBg';
import { api, formatApiError } from '@/lib/api';

const COUNTRY_CODES = [
  ['+91', 'IND'], ['+1', 'USA'], ['+44', 'GBR'], ['+61', 'AUS'], ['+64', 'NZL'],
  ['+81', 'JPN'], ['+82', 'KOR'], ['+86', 'CHN'], ['+65', 'SGP'], ['+60', 'MYS'],
  ['+62', 'IDN'], ['+66', 'THA'], ['+84', 'VNM'], ['+63', 'PHL'], ['+92', 'PAK'],
  ['+880', 'BGD'], ['+94', 'LKA'], ['+977', 'NPL'], ['+971', 'UAE'], ['+966', 'SAU'],
  ['+974', 'QAT'], ['+965', 'KWT'], ['+968', 'OMN'], ['+972', 'ISR'], ['+27', 'ZAF'],
  ['+20', 'EGY'], ['+234', 'NGA'], ['+254', 'KEN'], ['+212', 'MAR'], ['+33', 'FRA'],
  ['+49', 'DEU'], ['+39', 'ITA'], ['+34', 'ESP'], ['+31', 'NLD'], ['+41', 'CHE'],
  ['+43', 'AUT'], ['+46', 'SWE'], ['+47', 'NOR'], ['+45', 'DNK'], ['+7', 'RUS'],
  ['+55', 'BRA'], ['+52', 'MEX'], ['+54', 'ARG'], ['+56', 'CHL'], ['+57', 'COL'], ['+51', 'PER'],
];

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneError, setPhoneError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill name, email and message');
      return;
    }
    if (!form.phone) {
      setPhoneError('Phone Number is required');
      toast.error('Phone Number is required');
      return;
    }
    const isIndia = countryCode === '+91';
    if (!/^\d+$/.test(form.phone)) {
      setPhoneError('Only numeric digits are allowed');
      toast.error('Only numeric digits are allowed');
      return;
    }
    if ((isIndia && form.phone.length !== 10) || (!isIndia && (form.phone.length < 7 || form.phone.length > 15))) {
      const error = isIndia
        ? 'Phone Number must contain exactly 10 digits'
        : 'Phone Number must contain 7 to 15 digits';
      setPhoneError(error);
      toast.error(error);
      return;
    }
    setPhoneError('');
    setSubmitting(true);
    try {
      await api.post('/public/contact', {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone?.trim() ? `${countryCode}${form.phone.trim()}` : null,
        message: form.message.trim(),
      });
      toast.success('Thanks! Our team will reach out within 24 hours.');
      setForm({ name: '', email: '', phone: '', message: '' });
      setCountryCode('+91');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 radial-glow" />
        <ParticlesBg count={25} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-brand-cyan/90 font-mono">Contact</div>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight max-w-4xl font-semibold">
            Let&rsquo;s plan your <span className="text-brand-cyan">GenAI Automation</span> transition.
          </h1>
          <p className="mt-5 text-white/70 max-w-2xl">Book a free career consultation. We&rsquo;ll map a personalised 90-day roadmap based on your current role.</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Contact info */}
          <div className="lg:col-span-5 space-y-5">
            <div className="glass rounded-2xl p-6 flex items-start gap-4">
              <Phone className="w-5 h-5 text-brand-cyan flex-shrink-0 mt-1" />
              <div>
                <div className="text-[10px] tracking-widest uppercase font-mono text-white/50">Phone</div>
                <a href={`tel:${BRAND.phone}`} className="text-base font-semibold hover:text-brand-cyan">{BRAND.phone}</a>
              </div>
            </div>
            <div className="glass rounded-2xl p-6 flex items-start gap-4">
              <Mail className="w-5 h-5 text-brand-cyan flex-shrink-0 mt-1" />
              <div>
                <div className="text-[10px] tracking-widest uppercase font-mono text-white/50">Email</div>
                <a href={`mailto:${BRAND.email}`} className="text-base font-semibold hover:text-brand-cyan break-all">{BRAND.email}</a>
              </div>
            </div>
            <div className="glass rounded-2xl p-6 flex items-start gap-4">
              <MapPin className="w-5 h-5 text-brand-cyan flex-shrink-0 mt-1" />
              <div>
                <div className="text-[10px] tracking-widest uppercase font-mono text-white/50">Address</div>
                <div className="text-sm text-white/85 leading-relaxed">{BRAND.address}</div>
              </div>
            </div>

            <a href={BRAND.whatsapp} target="_blank" rel="noreferrer"
              className="glass rounded-2xl p-6 flex items-start gap-4 hover:border-[#25D366]/40 transition">
              <MessageCircle className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-1" />
              <div>
                <div className="text-[10px] tracking-widest uppercase font-mono text-white/50">WhatsApp</div>
                <div className="text-base font-semibold">Chat with mentor instantly</div>
              </div>
            </a>

            <div className="flex items-center gap-3 pt-2">
              <a href={BRAND.social.linkedin} target="_blank" rel="noreferrer" className="h-11 w-11 glass rounded-lg flex items-center justify-center hover:text-brand-cyan transition"><Linkedin className="w-4 h-4" /></a>
              <a href={BRAND.social.youtube} target="_blank" rel="noreferrer" className="h-11 w-11 glass rounded-lg flex items-center justify-center hover:text-brand-cyan transition"><Youtube className="w-4 h-4" /></a>
              <a href={BRAND.social.instagram} target="_blank" rel="noreferrer" className="h-11 w-11 glass rounded-lg flex items-center justify-center hover:text-brand-cyan transition"><Instagram className="w-4 h-4" /></a>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form onSubmit={onSubmit} className="glass-strong rounded-2xl p-6 sm:p-8 space-y-5">
              <h2 className="font-display text-2xl font-bold">Book a free consultation</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="name" label="Full Name" tid={CONTACT.nameInput} value={form.name} onChange={onChange('name')} placeholder="Your full name" />
                <Field id="email" label="Email" type="email" tid={CONTACT.emailInput} value={form.email} onChange={onChange('email')} placeholder="you@company.com" />
              </div>
              <div>
                <label htmlFor="phone" className="text-xs tracking-widest uppercase font-mono text-white/60">Phone</label>
                <div className="mt-1.5 flex items-stretch rounded-lg bg-white/5 border border-white/10 focus-within:border-brand-cyan/60 overflow-hidden">
                  <select
                    value={countryCode}
                    onChange={(e) => {
                      setCountryCode(e.target.value);
                      if (phoneError) setPhoneError('');
                    }}
                    aria-label="Country calling code"
                    className="w-28 shrink-0 bg-white/5 border-r border-white/10 px-2 py-3 text-sm text-white/80 focus:outline-none"
                  >
                    {COUNTRY_CODES.map(([code, name]) => (
                      <option key={code} value={code} className="bg-[#050A14]">{code} {name}</option>
                    ))}
                  </select>
                  <input
                    id="phone"
                    type="tel"
                    data-testid={CONTACT.phoneInput}
                    value={form.phone}
                    required
                    inputMode="numeric"
                    pattern={countryCode === '+91' ? '\\d{10}' : '\\d{7,15}'}
                    maxLength={countryCode === '+91' ? 10 : 15}
                    onChange={(e) => {
                      const maxLength = countryCode === '+91' ? 10 : 15;
                      setForm((f) => ({ ...f, phone: e.target.value.replace(/\D+/g, '').slice(0, maxLength) }));
                      if (phoneError) setPhoneError('');
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const text = (e.clipboardData || window.clipboardData).getData('text');
                      const maxLength = countryCode === '+91' ? 10 : 15;
                      setForm((f) => ({ ...f, phone: (text || '').replace(/\D+/g, '').slice(0, maxLength) }));
                    }}
                    onKeyDown={(e) => {
                      const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
                      if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return;
                      if (!/^\d$/.test(e.key)) e.preventDefault();
                    }}
                    placeholder={countryCode === '+91' ? '9876543210' : 'Phone number'}
                    className="w-full min-w-0 px-4 py-3 bg-transparent focus:outline-none placeholder:text-white/30"
                  />
                </div>
                {phoneError && <p className="mt-1 text-xs text-red-400">{phoneError}</p>}
              </div>
              <div>
                <label className="text-xs tracking-widest uppercase font-mono text-white/60">Tell us about your goals</label>
                <textarea
                  data-testid={CONTACT.messageInput}
                  value={form.message}
                  onChange={onChange('message')}
                  rows={5}
                  placeholder="Current role, target stack (Selenium / Playwright / Appium), timeline..."
                  className="mt-1.5 w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-brand-cyan/60 focus:outline-none placeholder:text-white/30 resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid={CONTACT.submitButton}
                className="w-full sm:w-auto bg-brand-cyan text-black font-semibold px-7 py-3.5 rounded-lg btn-glow hover:bg-brand-cyanSoft transition disabled:opacity-60"
              >
                {submitting ? 'Sending...' : 'Submit Request'}
              </button>
            </form>

            <div className="mt-6 glass rounded-2xl overflow-hidden">
              <iframe
                title="BYMAT location"
                src="https://www.google.com/maps?q=Lohagaon+Pune+411047&output=embed"
                className="w-full h-72 grayscale-[0.4] contrast-110"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const Field = ({ id, label, type = 'text', tid, value, onChange, onPaste, onKeyDown, placeholder, required, inputMode, pattern, maxLength, error }) => (
  <div>
    <label htmlFor={id} className="text-xs tracking-widest uppercase font-mono text-white/60">{label}</label>
    <input
      id={id}
      type={type}
      data-testid={tid}
      value={value}
      onChange={onChange}
      onPaste={onPaste}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      required={required}
      inputMode={inputMode}
      pattern={pattern}
      maxLength={maxLength}
      className="mt-1.5 w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-brand-cyan/60 focus:outline-none placeholder:text-white/30"
    />
    {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
  </div>
);

export default Contact;
